import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, LogOutIcon } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Field, TextInput } from '../components/ui/Field';
import { UnderlineTabs } from '../components/ui/Tabs';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
const modalTabs = [{
  value: 'account',
  label: 'Account Information'
}, {
  value: 'email',
  label: 'Email'
}, {
  value: 'password',
  label: 'Password'
}];
function PasswordInput({
  id,
  label,
  value,
  onChange
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  return <div data-id="element-455" data-mp="1ejv4mu3rk6m6">
      <label htmlFor={id} className="block text-[12.5px] font-medium text-ink-900" data-id="element-456" data-mp="1ejd8ryl0jues">
        {label}
      </label>
      <div className="mt-1.5 flex gap-2" data-id="element-457" data-mp="12yckws4fuauy">
        <TextInput id={id} value={value} onChange={onChange} type={visible ? 'text' : 'password'} data-id="element-458" data-mp="12zdyb38xf9fu" />
        <button type="button" onClick={() => setVisible(v => !v)} aria-label={visible ? `Hide ${label}` : `Show ${label}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-ink-200 text-ink-600 hover:bg-ink-50" data-id="element-459" data-mp="17vmhqsu5post">
          {visible ? <EyeOffIcon className="h-4 w-4" data-id="element-460" data-mp="k69zl3guq6ed" /> : <EyeIcon className="h-4 w-4" data-id="element-461" data-mp="ozcrl75zd7sg" />}
        </button>
      </div>
    </div>;
}
export function AccountSettings() {
  const {
    user,
    signOut,
    updateName
  } = useAuth();
  const {
    showToast
  } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('account');
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const passwordValid = currentPassword.length > 0 && newPassword.length >= 8 && newPassword === confirmPassword;
  function logout() {
    signOut();
    navigate('/');
  }
  return <>
      <PageHeader title="Account Settings" subtitle="Here you can find all the account settings" actions={<button type="button" onClick={logout} className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3.5 text-[13px] font-semibold text-brand-600 shadow-card hover:bg-ink-50" data-id="element-463" data-mp="2lxtn3v2kq6ih">
            <LogOutIcon className="h-4 w-4" data-id="element-464" data-mp="15j22i4bo63jq" />
            Logout
          </button>} data-id="element-462" data-mp="2qwn1rqtlk3k8" />

      <div className="flex-1 bg-white px-6 py-5" data-id="element-465" data-mp="11hgougrb9l8h">
        <section className="w-[325px] rounded-xl border border-ink-200 bg-white p-5 shadow-card" data-id="element-466" data-mp="11fvqsh5sfk5v">
          <div className="flex items-center gap-4" data-id="element-467" data-mp="3d6dcz4ingu6p">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-700 text-[17px] font-bold text-white" data-id="element-468" data-mp="3h649louuy4j5">
              {user?.initials}
            </span>
            <p className="min-w-0 flex-1 truncate text-[15px] font-bold text-ink-900" data-id="element-469" data-mp="3qw745thmnqjl">{user?.name}</p>
            <Button size="sm" onClick={() => setOpen(true)} data-id="element-470" data-mp="3vvjyt0eqq1wq">
              Edit Account
            </Button>
          </div>

          <div className="my-4 border-t border-ink-200" data-id="element-471" data-mp="4o7g2gy7lgep" />

          <dl className="space-y-2" data-id="element-472" data-mp="4qtoeisxbzxz">
            {[{
            label: 'Email',
            value: user?.email
          }, {
            label: 'First Name',
            value: user?.firstName
          }, {
            label: 'Last Name',
            value: user?.lastName
          }, {
            label: 'Role',
            value: user?.role
          }].map(row => <div key={row.label} className="flex items-baseline gap-4" data-id="element-473" data-mp="2fgs4ghy1akks">
                <dt className="w-[70px] shrink-0 text-[12.5px] text-ink-500" data-id="element-474" data-mp="2fj4nkyfj30zf">{row.label}</dt>
                <dd className="min-w-0 break-words text-[13px] font-medium text-ink-900" data-id="element-475" data-mp="1k8ycveg52771">{row.value}</dd>
              </div>)}
          </dl>
        </section>
      </div>

      <Modal open={open} title="Edit your account" onClose={() => setOpen(false)} width={390} data-id="element-476" data-mp="1u4j90vyj0d8v">
        <div className="px-6" data-id="element-477" data-mp="1p6hpk9x6ubwv">
          <UnderlineTabs label="Account sections" options={modalTabs} value={tab} onChange={setTab} data-id="element-478" data-mp="1p43xm9xt1ftj" />
        </div>

        <div className="min-h-[280px] space-y-4 px-6 pb-6 pt-5" data-id="element-479" data-mp="whj5365lexoe">
          {tab === 'account' && <>
              <Field label="First Name" htmlFor="acct-first" data-id="element-480" data-mp="1b6fb6xo3a0md">
                <TextInput id="acct-first" value={firstName} onChange={setFirstName} data-id="element-481" data-mp="1b807ostyrtmv" />
              </Field>
              <Field label="Last Name" htmlFor="acct-last" data-id="element-482" data-mp="ipw6v0s3bf2z">
                <TextInput id="acct-last" value={lastName} onChange={setLastName} data-id="element-483" data-mp="iobe9gpc667r" />
              </Field>
              <Button fullWidth className="mt-1" onClick={() => {
            updateName(firstName, lastName);
            setOpen(false);
            showToast('Account Updated', 'Your account information has been saved');
          }} data-id="element-484" data-mp="dqjb1qj8qxo9">
                Update Account
              </Button>
            </>}

          {tab === 'email' && <>
              <Field label="Email" htmlFor="acct-email" data-id="element-485" data-mp="3844g6p5xzrsz">
                <TextInput id="acct-email" value={email} onChange={setEmail} type="email" data-id="element-486" data-mp="382jr3f2ed12i" />
              </Field>
              <Button fullWidth className="mt-1" onClick={() => {
            setOpen(false);
            showToast('Verification Sent', `We sent a verification email to ${email}`);
          }} data-id="element-487" data-mp="2z3ya3ockpu3u">
                Send Verification Email
              </Button>
            </>}

          {tab === 'password' && <>
              <PasswordInput id="acct-current" label="Current Password" value={currentPassword} onChange={setCurrentPassword} data-id="element-488" data-mp="3udl1sgdv4daa" />
              <PasswordInput id="acct-new" label="New Password" value={newPassword} onChange={setNewPassword} data-id="element-489" data-mp="3kmgmtgo6ssfg" />
              <PasswordInput id="acct-confirm" label="Confirm Password" value={confirmPassword} onChange={setConfirmPassword} data-id="element-490" data-mp="3bn3b0hclbrrk" />
              <Button fullWidth className="mt-1" disabled={!passwordValid} onClick={() => {
            setOpen(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            showToast('Password Updated', 'Your password has been changed');
          }} data-id="element-491" data-mp="31nuo28g57dep">
                Update Password
              </Button>
            </>}
        </div>
      </Modal>
    </>;
}